import { z } from 'zod';

/**
 * AI test plan auditor — coverage report contracts (Migration
 * 073 / Delivery roadmap Tier 3b.5).
 *
 * The nightly worker compares the route set demanded by stories
 * (Story → Tests) with the route set actually exercised by runs
 * (Test impact analysis) and produces a coverage report per
 * project. The frontend renders the report; the notification
 * router fans out a weekly digest.
 */

export const StoryPrioritySchema = z.enum(['P0', 'P1', 'P2', 'P3']);
export type StoryPriority = z.infer<typeof StoryPrioritySchema>;

export const CoverageGapSchema = z.object({
  route: z.string(),
  severity: z.number().min(0).max(1),
  openStoryIds: z.array(z.string()),
  /** Highest priority among the open stories demanding this route. */
  topPriority: StoryPrioritySchema,
});
export type CoverageGap = z.infer<typeof CoverageGapSchema>;

export const CoverageSummarySchema = z.object({
  coverage: z.number().min(0).max(1),
  byPriority: z.record(StoryPrioritySchema, z.number().min(0).max(1)),
  totalRoutes: z.number().int().nonnegative(),
  coveredRoutes: z.number().int().nonnegative(),
  gapCount: z.number().int().nonnegative(),
});
export type CoverageSummary = z.infer<typeof CoverageSummarySchema>;

export const CoverageExpectedSchema = z.object({
  routes: z.array(z.string()),
  byStory: z.record(z.string(), z.array(z.string())),
});
export type CoverageExpected = z.infer<typeof CoverageExpectedSchema>;

export const CoverageActualSchema = z.object({
  routes: z.array(z.string()),
});
export type CoverageActual = z.infer<typeof CoverageActualSchema>;

export const CoverageReportSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  generatedAt: z.string(),
  expected: CoverageExpectedSchema,
  actual: CoverageActualSchema,
  gaps: z.array(CoverageGapSchema),
  summary: CoverageSummarySchema,
});
export type CoverageReport = z.infer<typeof CoverageReportSchema>;

export const GetLatestCoverageReportResponseSchema = z.object({
  report: CoverageReportSchema.nullable(),
});
export type GetLatestCoverageReportResponse = z.infer<typeof GetLatestCoverageReportResponseSchema>;

const PRIORITY_RANK: Record<StoryPriority, number> = { P0: 4, P1: 3, P2: 2, P3: 1 };

/**
 * Pure gap analyser — exported for unit tests + the worker.
 *
 * Inputs:
 *   - `stories`   — open stories with their declared routes + priority.
 *   - `runRoutes` — routes hit by at least one passing run in the window.
 *
 * Outputs the gap list and the summary (coverage %, per-priority,
 * counts). Sorted by severity (highest first).
 */
export function analyseCoverage(opts: {
  stories: { id: string; routes: string[]; priority: StoryPriority }[];
  runRoutes: string[];
}): { expected: CoverageExpected; actual: CoverageActual; gaps: CoverageGap[]; summary: CoverageSummary } {
  const expected = new Set<string>();
  const byStory: Record<string, string[]> = {};
  const routePriorities = new Map<string, StoryPriority>();
  const routeStories = new Map<string, Set<string>>();

  for (const story of opts.stories) {
    if (story.routes.length === 0) continue;
    byStory[story.id] = [...story.routes];
    for (const r of story.routes) {
      expected.add(r);
      const existing = routePriorities.get(r);
      if (!existing || PRIORITY_RANK[story.priority] > PRIORITY_RANK[existing]) {
        routePriorities.set(r, story.priority);
      }
      let set = routeStories.get(r);
      if (!set) {
        set = new Set();
        routeStories.set(r, set);
      }
      set.add(story.id);
    }
  }

  const actual = new Set(opts.runRoutes);
  const gaps: CoverageGap[] = [];
  for (const route of expected) {
    if (actual.has(route)) continue;
    const top = routePriorities.get(route) ?? 'P2';
    const stories = [...(routeStories.get(route) ?? [])];
    gaps.push({
      route,
      severity: PRIORITY_RANK[top] / 4,
      openStoryIds: stories,
      topPriority: top,
    });
  }
  gaps.sort((a, b) => b.severity - a.severity || a.route.localeCompare(b.route));

  const totalRoutes = expected.size;
  const coveredRoutes = totalRoutes - gaps.length;
  const coverage = totalRoutes === 0 ? 1 : coveredRoutes / totalRoutes;

  const byPriority: Record<StoryPriority, number> = { P0: 1, P1: 1, P2: 1, P3: 1 };
  const expectedByPriority: Record<StoryPriority, Set<string>> = {
    P0: new Set(),
    P1: new Set(),
    P2: new Set(),
    P3: new Set(),
  };
  for (const [route, priority] of routePriorities) expectedByPriority[priority].add(route);
  for (const p of ['P0', 'P1', 'P2', 'P3'] as StoryPriority[]) {
    const total = expectedByPriority[p].size;
    if (total === 0) {
      byPriority[p] = 1;
    } else {
      const hits = [...expectedByPriority[p]].filter((r) => actual.has(r)).length;
      byPriority[p] = hits / total;
    }
  }

  return {
    expected: { routes: [...expected].sort(), byStory },
    actual: { routes: [...actual].sort() },
    gaps,
    summary: {
      coverage,
      byPriority,
      totalRoutes,
      coveredRoutes,
      gapCount: gaps.length,
    },
  };
}
